import React from 'react';
import DateUtility from '../../../blocks/utils/date-utility';
import PrometTableFooter from './PrometTableFooter';

interface Column {
    headerName?: string | React.ReactNode;
    headerCell?: () => any;
    headerTooltip?: string;
    extraHeaderName?: string | React.ReactNode;
    extraHeaderColSpan?: number;
    extraHeaderFromColSpan?: boolean;
    extraHeaderTooltip?: string,
    accessor?: (row: any) => any;
    align?: 'center' | 'left' | 'right' | undefined;
    type?: string;
    className?: string;
    classNameAccessor?: (row: any) => string;
    cell?: (row: any) => any;
    width?: string;
    fixed?: boolean;
    visible?: boolean;
    totalSumInFooterValue?: any;
    totalSumInFooterLabel?: any;
}

interface Props {
    id?: any;
    data: any[];
    keyAccessor?: (row: any) => any;
    columns: Column[];
    total?: number;
    size?: number;
    page?: number;
    hideHeader?: any;
    hideFooter?: any;
    simpleTable?: boolean;
    wrapperClassName?: string;
    responsiveClassName?: string;
    onPageChange?: (pageNumber: number) => void;
    onSizeChange?: (resultsPerPage: number) => void;
    yesText?: string;
    noText?: string;
}

const PrometTable = (props: Props) => {
    const texts = {
        yes: props.yesText || 'Da',
        no: props.noText || 'Ne'
    }

    function onSizeChange(size: number) {
        if (!props.onSizeChange) return;
        props.onSizeChange(size);
    }

    function onPageChange(page: number) {
        if (!props.onPageChange) return;
        props.onPageChange(page);
    }


    function getCell(column: Column, row: any) {
        if (column.cell) {
            return column.cell(row);
        }

        var cellValue: React.ReactNode | string;
        var returnValue: React.ReactNode | string;
        if (column.accessor) {
            const value = column.accessor(row);
            if (column.type === 'date')
                cellValue = DateUtility.toString(value);
            else if (column.type === 'boolean')
                cellValue = value === true ? texts.yes : texts.no;
            else
                cellValue = value;
        }

        if (column.width)
            returnValue = <div style={{ width: column.width }}>{cellValue}</div>;
        else
            returnValue = cellValue;

        return returnValue;
    }

    function getHeaderCell(column: Column) {
        if (column.headerCell) {
            return column.headerCell();
        }

        return column.headerName;
    }

    function getExtraHeaderCell(column: Column) {
        if (column.extraHeaderName) {
            return column.extraHeaderName;
        }
        return getHeaderCell(column);
    }

    function getExtraHeaderColSpan(column: Column): number {
        if (!column.extraHeaderColSpan)
            return 1;
        return column.extraHeaderColSpan;
    }

    function getExtraHeaderRowSpan(column: Column): number {
        if (hasExtraHeader(column))
            return 1;
        return 2;
    }

    function getExtraHeaderToolTip(column: Column): string | undefined {
        if (hasExtraHeader(column))
            return column.extraHeaderTooltip;
        return column.headerTooltip;
    }

    function hasExtraHeader(column: Column): boolean {
        return !(!column.extraHeaderName && !column.extraHeaderFromColSpan);
    }

    function getTableWrapperClassName() {
        let className = 'promet-table';

        if (props.simpleTable)
            className += ' simple-table';

        if (props.wrapperClassName) className += ` ${props.wrapperClassName}`;

        return className;
    }

    function getTableResponsiveClassName() {
        let className = 'promet-table-responsive overflow-visible';

        if (props.responsiveClassName) className += ` ${props.responsiveClassName}`;

        return className;
    }

    function getHeaderClassName(column: Column): string {
        let className = '';

        if (column.fixed) className += 'fixed-col';

        return className;
    }

    function getRowKey(row: any, index: number): number | string {
        if (props.keyAccessor) return props.keyAccessor(row);
        return index;
    }

    function getRowClassName(column: Column, row: any): string {
        let className = '';

        if (column.align) className += `text-${column.align}`;
        else className += 'text-center';

        if (column.className) className += ` ${column.className}`;
        if (column.fixed) className += ' fixed-col';

        if (column.classNameAccessor && row) className += ` ${column.classNameAccessor(row)}`;

        return className;
    }

    function getFooterRowClassName(column: Column): string {
        return getRowClassName(column, undefined);
    }

    const { data, columns } = props;
    const renderingColumns = columns.filter(column => column.visible !== false);

    return (
        <div className={getTableWrapperClassName()}>
            <div className={getTableResponsiveClassName()}>
                <table className='promet-table-table' id={props.id}>
                    {
                        !props.hideHeader &&
                        < thead >
                            <tr>
                                {
                                    //Rendering extra header, or normal header if column doesn't have extra header
                                    renderingColumns && renderingColumns.map((column: Column, i) => (
                                        !column.extraHeaderFromColSpan &&
                                        <th key={i} scope='col' className={getHeaderClassName(column)} colSpan={getExtraHeaderColSpan(column)} rowSpan={getExtraHeaderRowSpan(column)} data-toggle="tooltip" data-html="true" title={getExtraHeaderToolTip(column)}>
                                            {getExtraHeaderCell(column)}
                                        </th>
                                    ))
                                }
                            </tr>
                            <tr>
                                {
                                    //Rendering normal header only if column has extra header
                                    renderingColumns && renderingColumns.map((column: Column, i) =>
                                        (hasExtraHeader(column)) && (
                                            <th key={i} scope='col' className={getHeaderClassName(column)} data-toggle="tooltip" data-html="true" title={column.headerTooltip}>
                                                {getHeaderCell(column)}
                                            </th>
                                        ))
                                }
                            </tr>
                        </thead>
                    }
                    <tbody>
                        {
                            data && data.map((row, i) => (
                                <tr key={getRowKey(row, i)}>
                                    {
                                        renderingColumns && renderingColumns.map((column: Column, i) => (
                                            <td key={i}
                                                className={getRowClassName(column, row)}>
                                                {getCell(column, row)}
                                            </td>
                                        ))
                                    }
                                </tr>
                            ))
                        }
                    </tbody>
                    {
                        renderingColumns && renderingColumns.some((column) => column.totalSumInFooterValue) &&
                        <tfoot>
                            {
                                <tr>
                                    {
                                        renderingColumns.map((column: Column, i) => (
                                            <td key={i}
                                                className={getFooterRowClassName(column)}
                                                {...(column.width && { width: column.width })}>
                                                {
                                                    column.totalSumInFooterLabel &&
                                                    column.totalSumInFooterLabel
                                                }
                                                {
                                                    column.totalSumInFooterValue &&
                                                    column.totalSumInFooterValue
                                                }
                                            </td>
                                        ))
                                    }
                                </tr>

                            }
                        </tfoot>
                    }
                </table>
                {
                    !props.hideFooter &&
                    <PrometTableFooter
                        onPageChange={onPageChange}
                        onSizeChange={onSizeChange}
                        total={props.total}
                        size={props.size}
                        page={props.page}
                        dataCount={data.length}
                    />
                }
            </div>
        </div >
    )
}

export default PrometTable;